import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, Input, Spinner, Table, Td, Th, ConfirmDialog } from '@/components/admin/ui';
import {
  UserLink,
} from '@/components';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type AdminUserItem = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  created_at: string;
};

type AdminUsersResponse = {
  items: AdminUserItem[];
  total: number;
  page: number;
  page_size: number;
};

type UserSearchItem = {
  id: string;
  email: string | null;
  display_name: string | null;
};

export const Route = createFileRoute('/admin/admin-users')({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);
  const [emailToAdd, setEmailToAdd] = useState('');

  const adminsQuery = useQuery({
    queryKey: ['admin', 'admin-users'],
    queryFn: () => apiJson<AdminUsersResponse>('/v1/admin/admin-users'),
  });

  const userSearchQuery = useQuery({
    queryKey: ['admin', 'users', 'search', searchQuery],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('q', searchQuery);
      params.set('page', '1');
      params.set('page_size', '10');
      params.set('status', 'active');
      return apiJson<{ items: UserSearchItem[] }>(`/v1/admin/users?${params.toString()}`);
    },
    enabled: searchQuery.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: (body: { user_id?: string; email?: string }) =>
      apiJson<AdminUserItem>('/v1/admin/admin-users', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'admin-users'] });
      setSearch('');
      setSearchQuery('');
      setEmailToAdd('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      apiJson<void>(`/v1/admin/admin-users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      setRemoveUserId(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'admin-users'] });
    },
  });

  if (adminsQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (adminsQuery.error) {
    return (
      <p className="text-sm text-red-600">
        {(adminsQuery.error as Error).message}
        <button
          type="button"
          className="ml-2 underline"
          onClick={() => void adminsQuery.refetch()}
        >
          Retry
        </button>
      </p>
    );
  }

  const items = adminsQuery.data?.items ?? [];

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Admin users</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          App accounts with mobile Settings developer tools: paywall testing toggle and
          live feature-flag state. Distinct from this dashboard&apos;s password login.
        </p>
      </div>

      <Card>
        <h2 className="mb-3 text-lg font-medium">Add admin</h2>
        <form
          className="mb-4 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = emailToAdd.trim();
            if (!trimmed) return;
            void addMutation.mutate({ email: trimmed });
          }}
        >
          <Input
            value={emailToAdd}
            onChange={(e) => setEmailToAdd(e.target.value)}
            placeholder="user@example.com"
            className="min-w-[240px] flex-1"
            type="email"
            autoComplete="off"
          />
          <Button type="submit" disabled={addMutation.isPending || !emailToAdd.trim()}>
            Add by email
          </Button>
        </form>
        {addMutation.isError ? (
          <p className="mb-3 text-sm text-red-600">{(addMutation.error as Error).message}</p>
        ) : null}

        <form
          className="mb-2 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSearchQuery(search.trim());
          }}
        >
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users by email, name, or id"
            className="min-w-[240px] flex-1"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        {userSearchQuery.data?.items.length ? (
          <ul className="mb-2 space-y-1 rounded-md bg-nova-muted p-2 text-sm">
            {userSearchQuery.data.items.map((u) => (
              <li key={u.id} className="flex items-center justify-between gap-2">
                <span>
                  <UserLink userId={u.id} displayName={u.display_name} email={u.email} />
                  {u.email ? ` · ${u.email}` : ''}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={addMutation.isPending}
                  onClick={() => void addMutation.mutate({ user_id: u.id })}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      <Card>
        <h2 className="mb-3 text-lg font-medium">
          Current admins{adminsQuery.data ? ` (${adminsQuery.data.total})` : ''}
        </h2>
        {items.length === 0 ? (
          <p className="text-sm text-nova-muted-foreground">No admin users yet.</p>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>User</Th>
                  <Th>Added</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.user_id}>
                    <Td>
                      <div className="text-sm">
                        <UserLink
                          userId={item.user_id}
                          displayName={item.display_name}
                          email={item.email}
                        />
                        {item.email ? (
                          <div className="text-nova-muted-foreground">{item.email}</div>
                        ) : null}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap text-sm text-nova-muted-foreground">
                      {formatAdminDateTime(item.created_at)}
                    </Td>
                    <Td className="text-right">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setRemoveUserId(item.user_id)}
                      >
                        Remove
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <div className="mt-4">
          <Button variant="secondary" onClick={() => void adminsQuery.refetch()}>
            Refresh
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={removeUserId != null}
        title="Remove admin?"
        message="This user will lose mobile Settings developer tools (paywall testing and feature-flag debug)."
        confirmLabel="Remove"
        loading={removeMutation.isPending}
        onCancel={() => {
          if (!removeMutation.isPending) setRemoveUserId(null);
        }}
        onConfirm={() => {
          if (removeUserId) void removeMutation.mutate(removeUserId);
        }}
      />
    </div>
  );
}
